import { useState, type FC } from "react";
import { Modal, Pressable } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import { Banner, Button, Checkbox, Input, Text, View } from "@visioni/a3s-ui/components";
import "@visioni/a3s-ui/theme";
import { nextStep, resendTicked, type AnnouncementSendFailure } from "./announcement-form";

export type { AnnouncementSendFailure } from "./announcement-form";

export interface AnnouncementSendDialogProps {
  readonly templateName: string;
  readonly subject: string;
  /** e.g. "Generated by jo@visioni.com.au, 12 Oct 2026, 10:04" */
  readonly listDescription: string;
  readonly recipientCount: number;
  readonly sending: boolean;
  readonly failure: AnnouncementSendFailure | undefined;
  readonly onSend: (options: { allowResend: boolean }) => void;
  readonly onClose: () => void;
}

/**
 * The last step before emailing a list: restates what is about to go out and
 * to how many people, and only enables Send once that count is typed in. It
 * cannot be closed while a send is in flight.
 */
export const AnnouncementSendDialog: FC<AnnouncementSendDialogProps> = ({
  templateName,
  subject,
  listDescription,
  recipientCount,
  sending,
  failure,
  onSend,
  onClose,
}) => {
  const [typedCount, setTypedCount] = useState("");
  /** The earlier send the resend box was ticked for; see `resendTicked`. */
  const [tickedFor, setTickedFor] = useState<string | undefined>(undefined);
  const allowResend = resendTicked(tickedFor, failure);
  const setAllowResend = (checked: boolean) => setTickedFor(checked ? failure?.previous?.sendId : undefined);

  const step = nextStep(failure);
  const countConfirmed = typedCount.trim() === String(recipientCount);
  const allowedAfterFailure = failure === undefined || step === "retry" || (step === "resendAllowed" && allowResend);
  const canSend = countConfirmed && !sending && allowedAfterFailure;
  const close = () => {
    if (!sending) onClose();
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={close}>
      <View style={styles.backdrop}>
        <Pressable style={StyleSheet.absoluteFill} onPress={close} disabled={sending} accessibilityLabel="Close" />
        <View style={styles.dialog} gap="xl">
          <Text textStyle="md/semibold" color="text">
            Send this announcement?
          </Text>

          <View gap="sm">
            <SummaryRow label="Template" value={templateName} />
            <SummaryRow label="Subject" value={subject} />
            <SummaryRow label="List" value={listDescription} />
            <SummaryRow label="Recipients" value={String(recipientCount)} />
          </View>

          <Input
            label={`Type ${String(recipientCount)} to confirm`}
            floatLabel="always"
            value={typedCount}
            onChangeText={setTypedCount}
            placeholder={String(recipientCount)}
            size="small"
            disabled={sending}
          />

          {failure !== undefined && <Banner variant="warning" message={failure.message} />}

          {step === "resendAllowed" && (
            <Pressable onPress={() => setAllowResend(!allowResend)} style={styles.checkRow} disabled={sending}>
              <Checkbox checked={allowResend} onChange={setAllowResend} disabled={sending} />
              <Text textStyle="sm/regular" color="text">
                Send it again anyway
              </Text>
            </Pressable>
          )}

          <View flexDirection="row" justifyContent="flex-end" gap="md">
            <Button
              label={step === "blocked" ? "Close" : "Cancel"}
              size="sm"
              variant="secondary"
              onPress={close}
              disabled={sending}
            />
            {step !== "blocked" && (
              <Button
                label={`Send to ${String(recipientCount)}`}
                size="sm"
                onPress={() => onSend({ allowResend })}
                disabled={!canSend}
                loading={sending}
                loadingText="Sending…"
              />
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const SummaryRow: FC<{ label: string; value: string }> = ({ label, value }) => (
  <View flexDirection="row" gap="md">
    <Text textStyle="sm/medium" color="textMuted" style={styles.summaryLabel}>
      {label}
    </Text>
    <Text textStyle="sm/regular" color="text" style={styles.summaryValue}>
      {value}
    </Text>
  </View>
);

const styles = StyleSheet.create((theme) => ({
  backdrop: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing["6xl"],
    backgroundColor: theme.colors.black[500],
  },
  dialog: {
    width: "100%",
    maxWidth: 480,
    padding: theme.spacing["6xl"],
    borderRadius: theme.spacing.xl,
    backgroundColor: theme.colors.surfaceCard,
  },
  summaryLabel: { width: 96 },
  summaryValue: { flex: 1 },
  checkRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
  },
}));
